import React, { useEffect, useState } from 'react';
import { MapPin, Mountain, ShieldAlert, X } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { setSeoMetadata } from '../../lib/seo';
import { resolveMediaUrl } from '../../lib/mediaUrl';
import { fetchJsonCached } from '../../lib/settingsCache';
import { useSiteFeatureFlags } from '../../hooks/useSiteFeatureFlags';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Map pop-up, mirroring the legacy map-icon behaviour. */
const MapModal = ({ trail, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);
  if (!trail) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" role="dialog" aria-modal="true"
      aria-label={`${trail.name} map`} data-testid="hiking-map-modal">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} data-testid="hiking-map-modal-overlay" />
      <div className="relative z-10 w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-bold text-slate-900 truncate">{trail.name}</h2>
          <button onClick={onClose} className="p-2 rounded text-slate-600 hover:text-slate-900 hover:bg-slate-200"
            aria-label="Close map" data-testid="hiking-map-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="bg-slate-100" style={{ height: 420 }}>
          <iframe src={trail.map_embed_url} title={`${trail.name} map`} className="w-full h-full"
            style={{ border: 0 }} allowFullScreen referrerPolicy="no-referrer-when-downgrade" loading="lazy"
            data-testid="hiking-map-iframe" />
        </div>
      </div>
    </div>
  );
};

const SafetyList = ({ title, items, icon: Icon, testId }) => {
  if (!items || items.length === 0) return null;
  return (
    <section className="bg-slate-50 border border-slate-200 rounded-2xl p-6" data-testid={testId}>
      <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-blue-600" />
        {title}
      </h2>
      <ul className="space-y-2 list-disc pl-5">
        {items.map((item, i) => (
          <li key={i} className="text-slate-700 leading-relaxed">{item}</li>
        ))}
      </ul>
    </section>
  );
};

/** One labelled attribute row, e.g. "Skill: Easy" */
const Attr = ({ label, value }) => {
  if (!value) return null;
  return (
    <p className="text-slate-700 leading-snug">
      <span className="font-bold text-slate-900">{label}:</span> {value}
    </p>
  );
};

const HikingPage = () => {
  const { hiking_enabled, _loaded } = useSiteFeatureFlags();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapTrail, setMapTrail] = useState(null);

  useEffect(() => {
    setSeoMetadata({
      title: 'Hiking in St John | Trails, Skill Levels & Trailheads | St John USVI',
      description:
        'Hiking trails in St John, USVI. Browse Virgin Islands National Park trails with skill level, duration, pet rules, trailhead locations and maps, plus trail safety tips.',
      keywords:
        'hiking st john, st john trails, virgin islands national park hiking, reef bay trail, ram head trail, st john usvi hiking',
      canonicalPath: '/island-info/hiking-in-st-john',
      ogType: 'website',
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetchJsonCached(`${API_URL}/api/public/hiking`);
        if (!cancelled) setData(res);
      } catch (error) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const page = data?.page || {};
  const trails = data?.trails || [];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main className="pt-28 lg:pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4">
          <header className="text-center mb-10">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold italic uppercase text-slate-900 tracking-tight">
              Hiking in St John
            </h1>
            <p className="font-script text-3xl text-cyan-500 mt-3">St John, Virgin Islands</p>
          </header>

          {loading || !_loaded ? (
            <div className="flex justify-center py-16" data-testid="hiking-loading">
              <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !hiking_enabled ? (
            <p className="text-center text-slate-500 py-16" data-testid="hiking-disabled">
              Our hiking guide is being updated. Please check back soon.
            </p>
          ) : (
            <>
              <div className="grid md:grid-cols-2 gap-6">
                <SafetyList
                  title={page.hike_safely_title || 'Hike Safely'}
                  items={page.hike_safely_items}
                  icon={Mountain}
                  testId="hiking-safely"
                />
                <SafetyList
                  title={page.trail_safety_title || 'Trail Safety'}
                  items={page.trail_safety_items}
                  icon={ShieldAlert}
                  testId="hiking-safety"
                />
              </div>

              {(page.intro_title || page.intro_body) && (
                <section className="mt-14 max-w-4xl mx-auto text-center" data-testid="hiking-intro">
                  {page.intro_title && (
                    <h2 className="text-3xl font-extrabold italic uppercase text-slate-900 tracking-tight">
                      {page.intro_title}
                    </h2>
                  )}
                  {page.intro_body && (
                    <div className="mt-5 space-y-4 text-slate-700 leading-relaxed text-left">
                      {page.intro_body.split('\n\n').map((para, i) => (
                        <p key={i}>{para}</p>
                      ))}
                    </div>
                  )}
                  {page.full_map_url && (
                    <a
                      href={page.full_map_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white font-bold rounded hover:bg-red-700 transition-colors"
                      data-testid="hiking-full-map-link"
                    >
                      <MapPin className="w-4 h-4" /> Full Hiking Locations Map
                    </a>
                  )}
                </section>
              )}

              <section className="mt-16" data-testid="hiking-trails-section">
                {page.trails_title && (
                  <h2 className="text-3xl font-extrabold italic uppercase text-slate-900 text-center tracking-tight mb-10">
                    {page.trails_title}
                  </h2>
                )}

                {trails.length === 0 ? (
                  <p className="text-center text-slate-500 py-12" data-testid="hiking-empty">
                    Trails are being added. Check back soon!
                  </p>
                ) : (
                  <div className="space-y-12 lg:space-y-16">
                    {trails.map((trail, index) => {
                      /* The legacy page alternates the panel colour row by row. */
                      const panel = index % 2 === 0
                        ? 'bg-[#3f3d9e]'
                        : 'bg-[#2e9ab5]';
                      return (
                        <article
                          key={trail.id}
                          className="grid lg:grid-cols-[minmax(0,1fr)_380px] gap-8 lg:gap-10 items-start"
                          data-testid={`hiking-trail-${trail.slug}`}
                        >
                          {/* photo overlapping the coloured copy panel */}
                          <div className="relative flex flex-col sm:flex-row items-stretch">
                            <div className="relative z-10 sm:w-[46%] shrink-0 shadow-xl">
                              {trail.image_url ? (
                                <img
                                  src={resolveMediaUrl(trail.image_url)}
                                  alt={trail.name}
                                  loading="lazy"
                                  className="w-full h-56 sm:h-full min-h-[240px] object-cover"
                                />
                              ) : (
                                <div className="w-full h-56 sm:h-full min-h-[240px] bg-slate-200 flex items-center justify-center text-slate-400">
                                  <Mountain className="w-10 h-10" />
                                </div>
                              )}
                            </div>

                            <div
                              className={`${panel} flex-1 shadow-2xl px-6 py-8 sm:-ml-10 sm:pl-16 sm:pr-8 flex flex-col justify-center`}
                            >
                              <h3 className="text-2xl lg:text-3xl font-bold text-white">{trail.name}</h3>
                              {trail.description && (
                                <p className="mt-4 text-white/90 leading-relaxed text-sm lg:text-base">
                                  {trail.description}
                                </p>
                              )}
                              {(trail.place_name || trail.address) && (
                                <p className="mt-4 text-white/75 text-xs">
                                  {[trail.place_name, trail.address].filter(Boolean).join(' \u2014 ')}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* map icon + attribute list */}
                          <div className="flex gap-5">
                            {(trail.map_embed_url || trail.map_url) && (
                              <div className="shrink-0 text-center">
                                <button
                                  onClick={() => (trail.map_embed_url
                                    ? setMapTrail(trail)
                                    : window.open(trail.map_url, '_blank', 'noopener'))}
                                  className="group"
                                  aria-label={`${trail.name} map`}
                                  data-testid={`hiking-viewmap-btn-${trail.slug}`}
                                >
                                  <img
                                    src="/images/hiking/map-icon.webp"
                                    alt=""
                                    aria-hidden="true"
                                    loading="lazy"
                                    className="w-16 h-16 mx-auto object-contain group-hover:scale-110 transition-transform"
                                  />
                                  <span className="block mt-1.5 text-sm font-bold text-slate-900 group-hover:text-blue-700">
                                    map
                                  </span>
                                </button>
                              </div>
                            )}

                            <div className="space-y-3 min-w-0">
                              <Attr label="Parks" value={trail.parks} />
                              <Attr label="Skill" value={trail.skill} />
                              <Attr label="Duration" value={trail.duration} />
                              <Attr label="Reservations" value={trail.reservations} />
                              <Attr label="Pets" value={trail.pets} />
                              <Attr label="Time Of Day" value={trail.time_of_day} />
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </main>

      {mapTrail && <MapModal trail={mapTrail} onClose={() => setMapTrail(null)} />}

      <Footer />
    </div>
  );
};

export default HikingPage;
